/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/ban-ts-ignore */
/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import * as React from "react";
import _ from "underscore";
import ScaleLoader from "react-spinners/ScaleLoader";
import { datadogRum } from "@datadog/browser-rum";

import * as API from "../../../api/manageCatalog";
import BasicTable from "../../reusable/reactBasicTable";
import Card from "../../reusable/cardv2";
import CaretMenuList from "../../reusable/caretMenuList";
import Checkbox from "../../reusable/checkbox";
import DenominationButton from "./components/denominationButton";
import EditCatalogItemIcon from "./components/editCatalogItemIcon";
import ToastMessage from "../../reusable/toastMessage";
import Tooltip from "../../reusable/tooltip";
import setUpAxios from "../../../helperFunctions/helperFunctions";
import {
  CatalogItem,
  Denomination,
  Provider,
  ToastMessageOptionTypes,
} from "./lib/types";
import {
  StyledArchivedCheckbox,
  StyledButtonCreateItem,
  StyledCapitalizedText,
  StyledCheckboxHeader,
  StyledContainer,
  StyledErrorMessageLeftMargin,
  StyledFilterRow,
  StyledHeaderRow,
  StyledPageHeader,
  StyledPageLoaderContainer,
  StyledParagraph,
  StyledParagraphNoMargin,
  StyledProviderSelect,
  StyledRowCheckboxWrapper,
  StyledSpan,
} from "./lib/styles";
import { initializeDatadogRUM } from "../../../utils";
import {
  defaultMessageOptions,
  inactiveReasonsDisplay,
  manageCatalogTableHeaders,
  providers,
} from "./lib/constants";
import {
  getDenominationState,
  getErrorMessage,
  getInactiveReasons,
} from "./lib/helpers";
import { theme } from "../../theme/theme";
import SelectInputField from "../../reusable/selectInputField";

const ManageCatalogContainer = (): React.ReactElement => {
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [data, setData] = React.useState<CatalogItem[]>([]);
  const [excludeArchived, setExcludeArchived] = React.useState<boolean>(true);
  const [provider, setProvider] = React.useState<Provider | "all">();
  const [
    toastMessageOptions,
    setToastMessageOptions,
  ] = React.useState<ToastMessageOptionTypes>(defaultMessageOptions);

  const updateToastMessage = (options: ToastMessageOptionTypes): void => {
    setToastMessageOptions(options);
  };

  const fetchData = async (): Promise<void> => {
    try {
      setErrorMessage("");
      setLoading(true);
      const params = {
        exclude: {
          archived: excludeArchived,
        },
        filters: {
          item_type: "core",
          provider: provider === "all" ? undefined : provider,
        },
      };
      const response = await API.getCatalogItems(params);
      const dataWithChecked = response.data.data.map((item) => {
        return {
          ...item,
          checked: false,
        };
      });
      setData(dataWithChecked);
    } catch (error) {
      const message = getErrorMessage(error);
      setData([]);
      setErrorMessage(
        `An error occurred while loading the data needed for this page. ${message}`
      );
      datadogRum.addError(error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    initializeDatadogRUM();
    setUpAxios();
  }, []);

  React.useEffect(() => {
    provider && fetchData();
  }, [excludeArchived, provider]);

  const handleHeaderCheckboxChange = (event): void => {
    const { checked } = event.target;
    const updatedData = data.map((item) => {
      return {
        ...item,
        checked,
      };
    });

    setData(updatedData);
  };

  const handleUpdateDenominations = async (
    nextState: "disabled" | "enabled"
  ): Promise<void> => {
    const updatedDenominations = [];
    const selectedItems = data.filter((item) => {
      return item.checked === true;
    });

    selectedItems.forEach((item) => {
      item.denominations.forEach((denomination) => {
        // NOTE: only update to new state if state has changed and state change is allowed
        if (getDenominationState(denomination.inactiveReasons) !== nextState) {
          // NOTE: reformatting here to match API format
          updatedDenominations.push({
            bufferable: denomination.bufferable,
            external_identifier: denomination.externalIdentifier,
            id: denomination.id,
            inactive_reasons: getInactiveReasons(
              nextState,
              denomination.inactiveReasons
            ),
            item_brand_name: item.brandName,
            item_id: denomination.itemId,
            legacy_id: denomination.legacyId,
            price_in_subunits: denomination.priceInSubunits,
          });
        }
      });
    });

    try {
      await API.updateDenominations(updatedDenominations);
    } catch (error) {
      datadogRum.addError(error);
      const message = (
        <>
          <p>An error occurred while updating denominations.</p>
          {error.errorMessages.map((message, index) => {
            if (index === error.errorMessages.length - 1) {
              return (
                <StyledParagraphNoMargin key={index}>
                  {message}
                </StyledParagraphNoMargin>
              );
            }
            return <p key={index}>{message}</p>;
          })}
        </>
      );

      updateToastMessage({
        isOpen: true,
        message,
        type: "error",
      });
    } finally {
      fetchData();
    }
  };

  const getTableHeaders = () => {
    const tableHeaders = [...manageCatalogTableHeaders];
    const checkboxHeaderIndex = tableHeaders.findIndex(
      (item) => item.accessor === "checkbox"
    );
    const selectedItems = data.filter((item) => {
      return item.checked === true;
    });

    tableHeaders[checkboxHeaderIndex].text = (
      <StyledCheckboxHeader>
        <StyledSpan>
          <Checkbox
            checked={selectedItems.length === data.length}
            handleChange={handleHeaderCheckboxChange}
            id="header-checkbox"
            indeterminate={
              selectedItems.length > 0 && selectedItems.length < data.length
            }
          />
        </StyledSpan>
        <CaretMenuList
          disabled={selectedItems.length === 0 || loading}
          menuItems={[
            {
              name: "Enable",
              handleClick: (): Promise<void> => {
                return handleUpdateDenominations("enabled");
              },
            },
            {
              name: "Disable",
              handleClick: (): Promise<void> => {
                return handleUpdateDenominations("disabled");
              },
            },
          ]}
        />
      </StyledCheckboxHeader>
    );

    return tableHeaders;
  };

  const handleLineItemCheckboxChange = (checked, item): void => {
    setData((oldData) => {
      return oldData.map((catalogItem) => {
        if (catalogItem.id === item.id) {
          return {
            ...catalogItem,
            checked,
          };
        } else {
          return catalogItem;
        }
      });
    });
  };

  const getDenominationsNote = (denominations: Denomination[]) => {
    const messages = [];

    denominations.forEach((denomination) => {
      if (denomination.state !== "archived") {
        // NOTE: inactiveReasons can be undefined
        const inactiveReasons = denomination.inactiveReasons || [];
        const inactiveReasonsFiltered = inactiveReasons.filter(
          (reason) => reason !== "admin_disabled"
        );
        if (inactiveReasonsFiltered.length) {
          const reasons = _.uniq(
            inactiveReasonsFiltered.map(
              (reason) => inactiveReasonsDisplay[reason] || "Unknown"
            )
          );
          messages.push(
            `${denomination.priceInSubunits / 100}: ${reasons.join(", ")}`
          );
        }
      }
    });

    const formattedMessages = messages.map((message) => {
      return <div>{message}</div>;
    });

    return formattedMessages;
  };

  const prepareRow = (item) => {
    item.checkbox = (
      <StyledRowCheckboxWrapper>
        <Checkbox
          checked={item.checked}
          handleChange={(event) =>
            handleLineItemCheckboxChange(event.target.checked, item)
          }
          id={`${item.id}-checkbox`}
        />
      </StyledRowCheckboxWrapper>
    );

    if (item.state === "archived") {
      item.statusIcon = (
        <Tooltip content="Deleted from all catalogs" placement="bottom">
          <i
            className="fas fa-exclamation-triangle"
            data-testid={`archived-icon-${item.id}`}
          />
        </Tooltip>
      );
    }

    item.providerDisplay = (
      <StyledCapitalizedText>
        {item.provider ? item.provider.replace("_", " ") : ""}
      </StyledCapitalizedText>
    );

    item.countriesDisplay = item.forAllCountries
      ? "All"
      : item.countries
          .map((country) => {
            return country.isoAlpha2Code;
          })
          .join(", ");

    item.distributionMethodDisplay = (
      <StyledCapitalizedText>
        {item.distributionMethod
          ? item.distributionMethod.replace("_", " ")
          : ""}
      </StyledCapitalizedText>
    );

    item.externalIdentifier = item.externalIdentifier || "--";

    item.denominationsDisplay = item.denominations ? (
      <>
        {item.denominations.map(
          (denomination, index): React.ReactChild => {
            return (
              <DenominationButton
                brandName={item.brandName}
                currency={item.currency}
                denomination={denomination}
                disabled={
                  item.state === "archived" || denomination.state === "archived"
                }
                key={index}
                updateToastMessage={updateToastMessage}
              />
            );
          }
        )}
      </>
    ) : null;

    item.denominationsNote = <>{getDenominationsNote(item.denominations)}</>;

    item.editIcon = (
      <EditCatalogItemIcon brandName={item.brandName} catalogItemId={item.id} />
    );

    return item;
  };

  return (
    <StyledContainer>
      <StyledHeaderRow>
        <StyledPageHeader>Manage Catalog</StyledPageHeader>
        <StyledButtonCreateItem
          onClick={(): void => {
            window.location.href = "/super_admin/items/new";
          }}
          size="small"
          testId="create-new-button"
          variant="primary"
        >
          Create New
        </StyledButtonCreateItem>
      </StyledHeaderRow>

      <Card bodyPadding="16px 0px" testId="catalog-items-container">
        <StyledFilterRow>
          <StyledProviderSelect>
            <SelectInputField
              id="provider-select"
              disabled={loading}
              handleChange={(event) => setProvider(event.target.value)}
              label="Provider"
              options={[...providers, { id: "all", key: "all", name: "All" }]}
              value={provider}
            />
          </StyledProviderSelect>
          <StyledArchivedCheckbox>
            <Checkbox
              checked={excludeArchived}
              disabled={loading}
              handleChange={(event) => setExcludeArchived(event.target.checked)}
              id="exclude-archived-checkbox"
              label="Exclude Archived Items"
            />
          </StyledArchivedCheckbox>
        </StyledFilterRow>

        {loading && (
          <StyledPageLoaderContainer data-testid="loader">
            <ScaleLoader
              color={theme.color.grayDark}
              loading={true}
              height={45}
              width={6}
              margin={6}
            />
          </StyledPageLoaderContainer>
        )}

        {!loading && !errorMessage && !provider && (
          <StyledParagraph data-testid="select-provider-message">
            Select a provider to view catalog items.
          </StyledParagraph>
        )}

        {!loading && errorMessage && (
          <StyledErrorMessageLeftMargin data-testid="error-message">
            {errorMessage}
          </StyledErrorMessageLeftMargin>
        )}

        {!loading && !errorMessage && data.length > 0 && (
          <BasicTable
            data={data}
            headers={getTableHeaders()}
            prepareRow={prepareRow}
          />
        )}

        {!loading && !errorMessage && provider && data.length === 0 && (
          <StyledParagraph data-testid="no-results-message">
            No catalog items found for the selected provider.
          </StyledParagraph>
        )}
      </Card>
      <ToastMessage
        closeToastMessage={(): void => {
          updateToastMessage({
            ...toastMessageOptions,
            isOpen: false,
          });
        }}
        isOpen={toastMessageOptions.isOpen}
        messageType={toastMessageOptions.type}
      >
        <span>{toastMessageOptions.message}</span>
      </ToastMessage>
    </StyledContainer>
  );
};

export default ManageCatalogContainer;
